import type { QueryClient } from '@tanstack/react-query';
import {
  ERP_CORE_CHANGED_EVENT,
  ERP_PIPELINE_EVENT,
  erpCoreChangedNoticeSchema,
  type CoreEntity,
} from '@evogard/contract';
import { registerRealtimeHandler } from '../../lib/realtime-bus.js';

/**
 * O que cada aviso do núcleo invalida — as telas do ERP que leem aquela
 * entidade, pelo prefixo da chave de consulta.
 *
 * Invalidar não é reler tudo: o TanStack só refaz as consultas ATIVAS (uma tela
 * aberta), e as outras ficam marcadas para quando forem abertas. É por isso
 * que o prefixo pode ser largo.
 *
 * A entidade que não está aqui não tem tela no ERP que a mostre, e o aviso
 * dela passa sem efeito. Tela nova que leia uma entidade entra aqui, junto com
 * a chave dela — senão ela fica no polling de reserva e ninguém percebe.
 */
// @valor
const AFFECTS: Record<CoreEntity, readonly (readonly string[])[]> = {
  associado: [
    ['erp', 'associado'],
    ['erp', 'associados'],
    ['erp', 'veiculo'],
    ['erp', 'veiculos'],
  ],
  voluntario: [],
  veiculo: [
    ['erp', 'veiculo'],
    ['erp', 'veiculos'],
    ['erp', 'associado'],
    ['erp', 'associados'],
  ],
  veiculo_produto: [
    ['erp', 'veiculo'],
    ['erp', 'produtos'],
    ['erp', 'produto-veiculos'],
  ],
  boleto: [
    ['erp', 'boleto'],
    ['erp', 'boletos'],
    ['erp', 'veiculo'],
    ['erp', 'associado'],
  ],
  boleto_veiculo: [
    ['erp', 'boleto'],
    ['erp', 'veiculo'],
  ],
  // vocabulário e situações aparecem em quase toda tela do ERP, e mudam uma
  // vez por noite: invalidar o módulo inteiro é barato e certo
  referencia: [['erp']],
  situacao_veiculo: [['erp']],
  situacao_boleto: [['erp']],
  situacao_motivo: [['erp']],
  /* o histórico do SGA: as telas dele e as fichas que mostram o cartão */
  atendimento: [
    ['erp', 'atendimento'],
    ['erp', 'atendimentos'],
    ['erp', 'associado'],
  ],
  alteracao: [
    ['erp', 'alteracao'],
    ['erp', 'alteracoes'],
    ['erp', 'associado'],
    ['erp', 'veiculo'],
    ['erp', 'boleto'],
  ],
};

/**
 * No máximo uma releitura a cada `WINDOW_MS` por prefixo: a primeira na hora,
 * e as que chegarem dentro da janela viram UMA no fim dela.
 *
 * As esteiras contínuas fazem a projeção de veículo e de boleto avisar quase a
 * cada passagem, e as duas costumam fechar perto uma da outra — sem isto, a
 * ficha do veículo releria duas vezes seguidas.
 */
const WINDOW_MS = 5_000;
const lastRun = new Map<string, number>();
const pending = new Map<string, ReturnType<typeof setTimeout>>();

function invalidate(client: QueryClient, queryKey: readonly string[]): void {
  const id = queryKey.join('/');
  if (pending.has(id)) return;
  const wait = (lastRun.get(id) ?? 0) + WINDOW_MS - Date.now();
  const run = () => {
    pending.delete(id);
    lastRun.set(id, Date.now());
    void client.invalidateQueries({ queryKey });
  };
  if (wait <= 0) run();
  else pending.set(id, setTimeout(run, wait));
}

registerRealtimeHandler(ERP_CORE_CHANGED_EVENT, (payload, client) => {
  // o aviso vem de fora do processo: o que não tem a forma do contrato é
  // ignorado, e nunca vira uma releitura do módulo inteiro por engano
  const notice = erpCoreChangedNoticeSchema.safeParse(payload);
  if (!notice.success) return;
  for (const key of AFFECTS[notice.data.entity]) invalidate(client, key);
});

registerRealtimeHandler(ERP_PIPELINE_EVENT, (_payload, client) => {
  invalidate(client, ['erp', 'saude']);
});
